// Cruce de un nombre de tienda de la programación contra la maestra de PDV.
//
// El archivo del comercial y la maestra NO escriben igual la misma tienda:
//   "MAKRO-SANTA-ANITA"  ↔  "Makro Santa Anita"
//   "SPSA-MAKRO-SURCO"   ↔  "Makro Surco"      (prefijo de cadena que la otra no usa)
//   "CUSCO-MK"           ↔  "Makro Cusco"      (abreviatura al final)
// Si el cruce falla, esa tienda entra como "no existe en la maestra" y alguien la crea
// duplicada; si el cruce se pasa de listo, las jornadas se cargan en la tienda equivocada.
// Por eso ante dos candidatos posibles se devuelve null: se reporta, no se adivina.

export interface PdvBasico {
  id: string;
  code: string;
  name: string;
}

const DIACRITICOS = /[\u0300-\u036f]/g;

/** Palabras del nombre, sin tildes ni signos: "SPSA-Makro Surco." → ['SPSA','MAKRO','SURCO']. */
function palabras(texto: string): string[] {
  return texto
    .normalize('NFD').replace(DIACRITICOS, '')
    .toUpperCase()
    .split(/[^A-Z0-9]+/)
    .filter((p) => p !== '');
}

/** Abreviaturas de cadena que usa el archivo del cliente y la maestra no ("CUSCO-MK" es
 *  "Makro Cusco"). Se amplía cuando aparezca otra: mientras no esté, la tienda se REPORTA
 *  como faltante, que es el fallo seguro. */
const ALIAS: Record<string, string> = { MK: 'MAKRO' };

const expandir = (ps: string[]): string[] => ps.map((p) => ALIAS[p] ?? p);

const iguales = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((p, i) => p === b[i]);

/** ¿`corto` es el final de `largo`? Exige 2 palabras: una sola ("SURCO") la comparten
 *  todas las tiendas de un distrito y emparejaría cualquier cosa. */
function terminaEn(largo: string[], corto: string[]): boolean {
  if (corto.length < 2 || corto.length > largo.length) return false;
  return iguales(largo.slice(largo.length - corto.length), corto);
}

/** Mismas palabras en cualquier orden, resolviendo las abreviaturas de cadena. */
function mismasPalabras(a: string[], b: string[]): boolean {
  const sa = new Set(expandir(a));
  const sb = new Set(expandir(b));
  if (sa.size < 2 || sa.size !== sb.size) return false;
  for (const p of sa) if (!sb.has(p)) return false;
  return true;
}

/**
 * Busca en la maestra la tienda que nombra la programación.
 * Devuelve null si no hay ninguna **o si hay más de una** en el mismo nivel de certeza.
 *
 * Los niveles se prueban en orden y el primero que responda decide: un nombre exacto
 * gana siempre sobre uno que solo comparte el final.
 */
export function emparejarTienda<T extends PdvBasico>(nombre: string, pdvs: T[]): T | null {
  const buscado = palabras(nombre);
  if (buscado.length === 0) return null;

  const candidatos = pdvs.map((pdv) => ({
    pdv,
    codigo: palabras(pdv.code),
    nombre: palabras(pdv.name),
  }));
  type Candidato = (typeof candidatos)[number];

  const niveles: ((c: Candidato) => boolean)[] = [
    (c) => iguales(c.codigo, buscado),
    (c) => iguales(c.nombre, buscado),
    (c) => terminaEn(buscado, c.nombre) || terminaEn(c.nombre, buscado),
    (c) => mismasPalabras(buscado, c.nombre),
  ];

  for (const nivel of niveles) {
    const encontrados = candidatos.filter(nivel);
    if (encontrados.length === 1) return encontrados[0].pdv;
    // Empate: bajar de nivel solo puede acertar por casualidad. Se reporta como faltante.
    if (encontrados.length > 1) return null;
  }
  return null;
}
